import { useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, Send, Clock, FileText, Lock } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

const StatusIcon = ({ status }) => {
  if (status === "done") return <CheckCircle2 className="w-4 h-4 text-mint" strokeWidth={1.5} />;
  if (status === "sent") return <Send className="w-4 h-4 text-sky-400" strokeWidth={1.5} />;
  return <Clock className="w-4 h-4 text-amber-400" strokeWidth={1.5} />;
};

const TabPanel = ({ tab }) => {
  return (
    <div className="grid lg:grid-cols-2 gap-10 items-center">
      <div>
        <h3 className="font-display font-semibold text-2xl lg:text-3xl text-white tracking-tight">{tab.title}</h3>
        <p className="mt-4 text-sm lg:text-base text-white/55 leading-relaxed">{tab.text}</p>
      </div>
      <div className="rounded-2xl border border-white/10 bg-night/70 backdrop-blur-xl p-6">
        {tab.requests && (
          <ul className="space-y-3">
            {tab.requests.map((r) => (
              <li key={r.name} className="flex items-center justify-between rounded-xl border border-white/5 bg-white/[0.03] px-4 py-3">
                <span className="text-sm text-white/80">{r.name}</span>
                <span className="flex items-center gap-2 text-xs text-white/50">
                  <StatusIcon status={r.status} />
                  {tab.statuses[r.status]}
                </span>
              </li>
            ))}
          </ul>
        )}
        {tab.breakdown && (
          <div>
            <p className="font-display text-4xl font-semibold text-mint">{tab.estimated}</p>
            <p className="text-xs text-white/40 mt-1 mb-6">{tab.estimatedLabel}</p>
            <ul className="space-y-4">
              {tab.breakdown.map((b, i) => (
                <li key={b.label}>
                  <div className="flex justify-between text-xs text-white/60 mb-1.5">
                    <span>{b.label}</span>
                    <span className="text-white/90 font-medium">{b.value}</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${90 - i * 22}%` }}
                      transition={{ duration: 1, delay: 0.2 + i * 0.15, ease: EASE }}
                      className="h-full rounded-full bg-mint/70"
                    />
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
        {tab.docs && (
          <ul className="space-y-3">
            {tab.docs.map((d) => (
              <li key={d.name} className="flex items-center justify-between rounded-xl border border-white/5 bg-white/[0.03] px-4 py-3">
                <span className="flex items-center gap-3">
                  <FileText className="w-4 h-4 text-white/40" strokeWidth={1.5} />
                  <span>
                    <span className="block text-sm text-white/80">{d.name}</span>
                    <span className="block text-[11px] text-white/40">{d.meta}</span>
                  </span>
                </span>
                <Lock className="w-4 h-4 text-mint/70" strokeWidth={1.5} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export const ProductDemo = () => {
  const { t } = useTranslation();
  const tabs = t("demo.tabs", { returnObjects: true });
  const [active, setActive] = useState(0);

  return (
    <section id="demo" data-testid="product-demo-section" className="py-24 lg:py-36 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: EASE }}
        >
          <p className="text-xs font-medium uppercase tracking-[0.25em] text-mint mb-4">{t("demo.kicker")}</p>
          <h2 className="font-display font-semibold tracking-tighter text-white text-3xl sm:text-4xl lg:text-5xl max-w-2xl text-balance">
            {t("demo.title")}
          </h2>
          <p className="mt-4 text-base md:text-lg text-white/55 max-w-xl">{t("demo.sub")}</p>
        </motion.div>

        <div className="mt-12 flex flex-wrap gap-2" role="tablist">
          {tabs.map((tab, i) => (
            <button
              key={tab.id}
              role="tab"
              aria-selected={active === i}
              data-testid={`demo-tab-${tab.id}`}
              onClick={() => setActive(i)}
              className={`rounded-full px-5 py-2.5 text-sm font-medium transition-colors duration-200 ${
                active === i ? "bg-mint text-night" : "border border-white/10 text-white/60 hover:text-white hover:border-white/25"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="mt-12 rounded-3xl border border-white/8 bg-card p-8 lg:p-14 min-h-[340px]">
          <AnimatePresence mode="wait">
            <motion.div
              key={active}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.45, ease: EASE }}
            >
              <TabPanel tab={tabs[active]} />
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};
